import { ChangeDetectorRef, Directive, ElementRef, HostBinding, HostListener, Input } from '@angular/core';

// A frame under the blur while it is the preview's, with its watermark (renderFrameVeiled): the
// class `is-veiled` on the image, which the page's styles blur. Lifted only once the clean frame has
// loaded — a new src keeps the old picture on screen until the new one is ready, so lifting with the
// src would flash the watermark for as long as the clean frame takes to arrive.
@Directive({ selector: 'img[appRenderVeil]' })
export class RenderVeilDirective {
  @HostBinding('class.is-veiled') public veiled = false;

  // The src the blur was over, and whether it is waiting for a new one to load before it goes.
  private veiledSrc = '';
  private lifting = false;

  constructor(
    private readonly element: ElementRef<HTMLImageElement>,
    private readonly changes: ChangeDetectorRef,
  ) {}

  @Input() set appRenderVeil(on: boolean) {
    if (on) {
      this.veiled = true;
      this.lifting = false;
      // After the pass that set it: the src binding may come before or after this one.
      void Promise.resolve().then(() => (this.veiledSrc = this.element.nativeElement.src));
      return;
    }
    if (!this.veiled || this.lifting) {
      return;
    }
    this.lifting = true;
    void Promise.resolve().then(() => {
      // The same picture (the blur went for another reason): nothing to wait for.
      if (this.lifting && this.element.nativeElement.src === this.veiledSrc) {
        this.lift();
      }
    });
  }

  @HostListener('load')
  @HostListener('error')
  public onSettled(): void {
    if (this.lifting) {
      this.lift();
    } else if (this.veiled) {
      this.veiledSrc = this.element.nativeElement.src;
    }
  }

  private lift(): void {
    this.lifting = false;
    this.veiled = false;
    this.changes.markForCheck();
  }
}
